import { StrictMode, Suspense, lazy } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";

import App from "./App";
// Side-effect import: applies persisted/system theme to <html> at
// module-load time, before React mounts. Prevents FOUC where the
// page paints in light mode then snaps to dark.
import "./lib/theme";
import { AuthGuard } from "./components/AuthGuard";
import { RouteFallback } from "./components/RouteFallback";
import { LoginPage } from "./pages/Login";
import { SignupPage } from "./pages/Signup";
import { ForgotPasswordPage } from "./pages/ForgotPassword";
import { ResetPasswordPage } from "./pages/ResetPassword";
import { AcceptInvitePage } from "./pages/AcceptInvite";
import "./index.css";

// Lazy-loaded routes. The operator's main `/` panneau workspace is
// statically imported above; admin and rarely-visited pages are
// fetched on demand to keep first-paint small. Pages use named
// exports so we adapt them to React.lazy's default-export shape.
const ProfileSettingsPage = lazy(() =>
  import("./pages/ProfileSettings").then((m) => ({
    default: m.ProfileSettingsPage,
  })),
);
const AdminProfilesPage = lazy(() =>
  import("./pages/AdminProfiles").then((m) => ({
    default: m.AdminProfilesPage,
  })),
);
const AdminPerformancePage = lazy(() =>
  import("./pages/AdminPerformance").then((m) => ({
    default: m.AdminPerformancePage,
  })),
);
const AdminOpeningHoursPage = lazy(() =>
  import("./pages/AdminOpeningHours").then((m) => ({
    default: m.AdminOpeningHoursPage,
  })),
);
const AdminPlanningPage = lazy(() =>
  import("./pages/AdminPlanning").then((m) => ({
    default: m.AdminPlanningPage,
  })),
);
const AdminSettingsPage = lazy(() =>
  import("./pages/AdminSettings").then((m) => ({
    default: m.AdminSettingsPage,
  })),
);
const AdminSuppliersPage = lazy(() =>
  import("./pages/AdminSuppliers").then((m) => ({
    default: m.AdminSuppliersPage,
  })),
);
const CounterpartiesPage = lazy(() =>
  import("./pages/Counterparties").then((m) => ({
    default: m.CounterpartiesPage,
  })),
);
const AdminUnidentifiedProductsPage = lazy(() =>
  import("./pages/AdminUnidentifiedProducts").then((m) => ({
    default: m.AdminUnidentifiedProductsPage,
  })),
);
const AdminCanonicalAliasesPage = lazy(() =>
  import("./pages/AdminCanonicalAliases").then((m) => ({
    default: m.AdminCanonicalAliasesPage,
  })),
);
const AdminConsignesPage = lazy(() =>
  import("./pages/AdminConsignes").then((m) => ({
    default: m.AdminConsignesPage,
  })),
);
const AdminChannelIdentitiesPage = lazy(() =>
  import("./pages/AdminChannelIdentities").then((m) => ({
    default: m.AdminChannelIdentitiesPage,
  })),
);
const CommandesPage = lazy(() =>
  import("./pages/Commandes").then((m) => ({ default: m.CommandesPage })),
);
const HelpPage = lazy(() =>
  import("./pages/Help").then((m) => ({ default: m.HelpPage })),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/* Public auth screens */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/accept-invite" element={<AcceptInvitePage />} />

          {/* Authenticated app */}
          <Route
            path="/profile"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteFallback />}>
                  <ProfileSettingsPage />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/admin/parametrage"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteFallback />}>
                  <AdminSettingsPage />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/admin/profils"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteFallback />}>
                  <AdminProfilesPage />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/admin/performance"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteFallback />}>
                  <AdminPerformancePage />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/admin/horaires"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteFallback />}>
                  <AdminOpeningHoursPage />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/admin/planning"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteFallback />}>
                  <AdminPlanningPage />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/admin/fournisseurs"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteFallback />}>
                  <AdminSuppliersPage />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/admin/contreparties"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteFallback />}>
                  <CounterpartiesPage />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/admin/produits-non-identifies"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteFallback />}>
                  <AdminUnidentifiedProductsPage />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/admin/alias-produits"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteFallback />}>
                  <AdminCanonicalAliasesPage />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/admin/consignes"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteFallback />}>
                  <AdminConsignesPage />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/admin/identites-externes"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteFallback />}>
                  <AdminChannelIdentitiesPage />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/commandes"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteFallback />}>
                  <CommandesPage />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/aide"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteFallback />}>
                  <HelpPage />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/"
            element={
              <AuthGuard>
                <App />
              </AuthGuard>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
